<script setup>
// @ts-nocheck
import { useI18n } from 'vue-i18n'
import { ref, nextTick, watch } from 'vue'
import { useLocalStorage, useMediaQuery, useWindowSize, onClickOutside } from '@vueuse/core'

const { locale } = useI18n()
const isOpen = ref(false)
const dropdownRef = ref(null)
const buttonRef = ref(null)
const menuStyle = ref({})

const languages = [
  { code: 'en', name: 'English', label: 'EN' },
  { code: 'es', name: 'Español', label: 'ES' },
  { code: 'ca', name: 'Català', label: 'CAT' },
  { code: 'gl', name: 'Galego', label: 'GL' },
  { code: 'eu', name: 'Euskara', label: 'EU' }
]

const isMobile = useMediaQuery('(max-width: 639px)')
const isDesktop = useMediaQuery('(min-width: 1024px)')
const { width: windowWidth, height: windowHeight } = useWindowSize()

const userLocale = useLocalStorage('user-locale', 'en')

const currentLang = () => languages.find(l => l.code === locale.value) || languages[0]

const switchLanguage = (code) => {
  locale.value = code
  userLocale.value = code
  isOpen.value = false
}

const calculatePosition = () => {
  if (!buttonRef.value || !isDesktop.value) {
    menuStyle.value = {}
    return
  }

  const button = buttonRef.value.getBoundingClientRect()
  const menuWidth = 192
  const menuHeight = languages.length * 48 + 16
  const viewportWidth = windowWidth.value
  const viewportHeight = windowHeight.value
  const padding = 16
  const offset = 8

  let left = button.right - menuWidth
  let top = button.top - menuHeight - offset

  if (left < padding) {
    left = padding
  }
  if (left + menuWidth > viewportWidth - padding) {
    left = viewportWidth - menuWidth - padding
  }
  if (top < padding) {
    top = button.bottom + offset
  }
  if (top + menuHeight > viewportHeight - padding) {
    top = viewportHeight - menuHeight - padding
  }

  menuStyle.value = {
    position: 'fixed',
    left: `${left}px`,
    top: `${top}px`,
    width: `${menuWidth}px`,
    zIndex: 50
  }
}

onClickOutside(dropdownRef, () => {
  isOpen.value = false
})

watch(isOpen, async (newVal) => {
  if (newVal && isDesktop.value) {
    await nextTick()
    calculatePosition()
  }
})

defineProps({
  showFullName: {
    type: Boolean,
    default: false
  }
})
</script>

<template>
  <div class="relative" ref="dropdownRef">
    <button ref="buttonRef" @click="isOpen = !isOpen"
      class="flex items-center gap-1 px-2 py-1.5 rounded-lg text-brand-muted hover:text-brand-accent hover:bg-brand-muted/10 transition-colors text-sm font-medium"
      :aria-label="$t('nav.language')" :aria-expanded="isOpen">
      <span :class="{'uppercase font-bold': !showFullName, 'font-bold': showFullName}">{{ showFullName ? currentLang().name : currentLang().label }}</span>
      <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 transition-transform" :class="{ 'rotate-180': isOpen }" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <!-- Mobile: Bottom Sheet -->
    <Teleport to="body">
      <Transition enter-active-class="transition-opacity duration-200 ease-out" enter-from-class="opacity-0" enter-to-class="opacity-100" leave-active-class="transition-opacity duration-150 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
        <div v-if="isOpen && isMobile" class="fixed inset-0 bg-black/50 z-[120] lg:hidden" aria-hidden="true"></div>
      </Transition>
      <Transition enter-active-class="transition-transform duration-250 ease-out" enter-from-class="translate-y-full" enter-to-class="translate-y-0" leave-active-class="transition-transform duration-200 ease-in" leave-from-class="translate-y-0" leave-to-class="translate-y-full">
        <div v-if="isOpen && isMobile" class="fixed bottom-0 left-0 right-0 bg-brand-surface z-[130] rounded-t-[28px] shadow-2xl shadow-black/25 lg:hidden" role="dialog" aria-modal="true" :aria-label="$t('nav.language')">
          <div class="p-4 border-b border-brand-muted/10 flex justify-between items-center">
            <span class="text-lg font-bold text-brand-text">{{ $t('nav.language') }}</span>
            <button @click="isOpen = false" class="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-brand-muted hover:text-brand-text rounded-full hover:bg-brand-muted/10 transition-colors" :aria-label="$t('common.close')">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
            <button v-for="lang in languages" :key="lang.code" @click="switchLanguage(lang.code)" :class="[
              'w-full flex items-center gap-4 px-5 py-4 rounded-[16px] text-left transition-all',
              locale === lang.code
                ? 'bg-brand-accent/10 text-brand-accent font-semibold'
                : 'text-brand-text hover:bg-brand-muted/5'
            ]">
              <span class="font-bold w-10 text-brand-muted">{{ lang.label }}</span>
              <span class="flex-1">{{ lang.name }}</span>
              <svg v-if="locale === lang.code" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand-accent" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </button>
          </div>
          <div class="pb-8"></div>
        </div>
      </Transition>
    </Teleport>

    <!-- Desktop: Dropdown -->
    <Transition enter-active-class="transition duration-150 ease-out" enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100" leave-active-class="transition duration-100 ease-in"
      leave-from-class="transform scale-100 opacity-100" leave-to-class="transform scale-95 opacity-0">
      <div v-show="isOpen && !isMobile"
        :style="menuStyle"
        class="bg-brand-surface rounded-2xl shadow-xl shadow-black/20 border border-brand-muted/10 overflow-hidden"
        :class="{ 'absolute right-0 bottom-full mb-2': !Object.keys(menuStyle).length }">
        <div class="py-2">
          <button v-for="lang in languages" :key="lang.code" @click="switchLanguage(lang.code)" :class="[
            'w-full flex items-center gap-3 px-4 py-3 min-h-[48px] text-sm transition-colors text-left',
            locale === lang.code
              ? 'bg-brand-accent/10 text-brand-accent font-semibold'
              : 'text-brand-text hover:bg-brand-muted/10 hover:text-brand-accent'
          ]">
            <span class="font-bold w-8">{{ lang.label }}</span>
            <span>{{ lang.name }}</span>
            <svg v-if="locale === lang.code" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-auto text-brand-accent" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>
